import { useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { Formik } from "formik";
import { Button } from "components/DesignSystem/Button/Button";
import { HelperText } from "components/DesignSystem/HelperText/HelperText";
import { Spinner } from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import {
  useUpdateChaserBatchMessageMutation,
  useGetChaseDraftMutation,
  useChaseDraftQuery,
} from "store/apis/agents";
import { ChaserBatch } from "types/Models/agentDashboard";
import { Refresh } from "components/icons/Refresh";
import { pluralize } from "utils/pluralize";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import dayjs from "dayjs";

type OnDemandPreview = {
  companyGroupId: string;
  companyGroupName: string;
  items: Array<{ metric_type: string; item_uuid: string }>;
  draft: string;
  channelUrl: string;
};

type BatchModeProps = {
  isOpen: boolean;
  onClose: () => void;
  batch: ChaserBatch;
  onViewChannel?: (channelId: string) => void;
  onDemandPreview?: never;
  onSaveDraft?: never;
};

type OnDemandModeProps = {
  isOpen: boolean;
  onClose: () => void;
  onDemandPreview: OnDemandPreview;
  onSaveDraft: (message: string) => void;
  onViewChannel?: never;
  batch?: never;
};

type Props = BatchModeProps | OnDemandModeProps;

export const ChaserPreviewModal = ({
  isOpen,
  onClose,
  batch,
  onViewChannel,
  onDemandPreview,
  onSaveDraft,
}: Props) => {
  const isBatchMode = batch !== undefined;

  const { alertToast } = useToast();

  const [localMessage, setLocalMessage] = useState<string | undefined>(
    undefined
  );
  const [regenerateError, setRegenerateError] = useState(false);

  const [updateMessage, { isLoading: isUpdating }] =
    useUpdateChaserBatchMessageMutation();
  const [getChaseDraft, { isLoading: isRegenerating }] =
    useGetChaseDraftMutation();

  const autoDraftArgs = isBatchMode
    ? {
        company_group_id: batch.company_group.uuid,
        items: batch.items.map((i) => ({
          metric_type: i.metric_type,
          item_uuid: i.item_uuid,
        })),
      }
    : skipToken;
  const {
    data: autoDraft,
    isLoading: isAutoFetching,
    isError: isAutoFetchError,
  } = useChaseDraftQuery(autoDraftArgs, { refetchOnMountOrArgChange: true });

  const initialMessage = isBatchMode
    ? batch.message_text ?? ""
    : onDemandPreview?.draft ?? "";

  const message = localMessage ?? (initialMessage || autoDraft?.draft) ?? "";

  const isGenerating = isRegenerating || isAutoFetching;
  const isLoading = isUpdating || isGenerating;
  const hasGenerateError = regenerateError || isAutoFetchError;

  const groupName = isBatchMode
    ? batch.company_group.name
    : onDemandPreview?.companyGroupName ?? "";

  const itemCount = isBatchMode
    ? batch.items.length
    : onDemandPreview?.items.length ?? 0;

  const channelName = (() => {
    if (!isBatchMode) return null;
    if (itemCount === 1) {
      return (
        batch.items[0]?.item_channel_name ??
        batch.company_group.secondary_channel_name ??
        null
      );
    }
    return batch.company_group.secondary_channel_name ?? null;
  })();

  const singleItemChannelUrl =
    isBatchMode && itemCount === 1
      ? batch.items[0]?.item_channel_url ?? null
      : null;

  const sendDate = isBatchMode
    ? dayjs(batch.scheduled_send_at).format("MMM D, YYYY [at] h:mm A")
    : null;

  const subtitle = isBatchMode
    ? `Edit the message before it auto-sends on ${sendDate}${
        channelName ? ` in "${channelName}"` : ""
      }.`
    : `${pluralize(
        itemCount,
        "item",
        "items"
      )} will be included in this message.`;

  const handleRegenerate = async () => {
    const companyGroupId = isBatchMode
      ? batch.company_group.uuid
      : onDemandPreview?.companyGroupId ?? "";

    const items = isBatchMode
      ? batch.items.map((i) => ({
          metric_type: i.metric_type,
          item_uuid: i.item_uuid,
        }))
      : onDemandPreview?.items ?? [];

    setRegenerateError(false);

    try {
      const result = await getChaseDraft({
        company_group_id: companyGroupId,
        items,
      }).unwrap();
      setLocalMessage(result.draft);
    } catch (error: unknown) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as {}
      );
      setRegenerateError(true);
    }
  };

  const handleSave = async () => {
    if (isBatchMode) {
      await updateMessage({
        batchId: batch.uuid,
        message_text: message,
      }).unwrap();
    } else {
      onSaveDraft?.(message);
    }
    onClose();
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="large">
        <Modal.Header>
          <Modal.Title>Preview message — {groupName}</Modal.Title>
          <Modal.Subtitle>{subtitle}</Modal.Subtitle>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={{ message }}
            onSubmit={() => {}}
            enableReinitialize
          >
            <div className="t-relative">
              <TextArea
                name="message"
                block
                value={message}
                onChange={(e) => setLocalMessage(e.target.value)}
                placeholder="Message to client..."
                disabled={isGenerating}
              />
              {isGenerating && (
                <div className="t-absolute t-inset-0 t-flex t-items-center t-justify-center t-bg-white/70 t-rounded-lg">
                  <Spinner size="small" />
                </div>
              )}
            </div>
          </Formik>
          <div className="t-mt-2 t-flex t-items-center t-justify-between">
            <div className="t-flex t-flex-col t-gap-0.5">
              <p className="t-text-caption t-text-text-30">
                {pluralize(itemCount, "item", "items")} included in this
                message.
              </p>
              {singleItemChannelUrl && onViewChannel && (
                <Button
                  customType="link"
                  size="small"
                  onClick={() => {
                    onClose();
                    onViewChannel(singleItemChannelUrl);
                  }}
                >
                  View channel ↗
                </Button>
              )}
            </div>
            <Button
              customType="ghost"
              size="small"
              onClick={handleRegenerate}
              disabled={isLoading}
              leftIcon={<Refresh size="14" />}
            >
              Regenerate
            </Button>
          </div>
          {hasGenerateError && (
            <HelperText variant="error">Unable to generate message</HelperText>
          )}
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.Close asChild>
            <Button customType="ghost">Cancel</Button>
          </Modal.Close>
          <Button
            customType="primary"
            onClick={handleSave}
            isLoading={isLoading}
            disabled={!message.trim()}
          >
            Save
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
